import { openInMacAppStore } from '../../../../util/mac-app-store';
import { Component } from '@angular/core';
import { STORE_APPS, storeAppListPrice } from 'src/app/data/product/product-lookup.data';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { CartPopupService } from 'src/app/service/cart/cart-popup.service';
import { StoreService } from 'src/app/service/store/store.service';
import { URLS } from '../../../../data/navigation/navigation.data';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';
import { ProductFeature, ProductScene } from '../../../../data/product/product-features.data';
import { TIDY_DESKTOP_FEATURES, tidyDesktopSceneSrc, tidyDesktopShot, tidyDesktopShots } from '../../../../data/product/tidy-desktop-features.data';

// Promo landing page for Tidy Desktop: AI Organizer, the desktop organizer app
// for macOS, served at /plugins/macos/tidy-desktop. Meant to be the app's
// marketing URL in App Store Connect, so it has to stay live and stay accurate.
// SEO is applied by the NavigationInterceptor from navigation.data.ts (client)
// and seo-route-meta.js (server). The copy describes only what the app actually
// does. Laid out as every Apps & Plugins page (../product-page.scss) and like the Dock Town page
// (owner 2026-10-10): a tidy desktop on the stage, then one section per thing the app does (the
// App Store pictures' headlines as text, their raw screens as pictures of their own), the rest as
// boxes, the questions, and the store button with the privacy policy at the foot; the pictures'
// lightbox is the shared one (../product-shots.ts).
@Component( {
  selector: 'app-macos-tidy-desktop-page',
  templateUrl: './macos-tidy-desktop-page.component.html',
  styleUrls: [
    '../chrome-color-picker-page/chrome-color-picker-page.component.scss',
    '../product-page.scss',
    // the feature sections are the Dock Town page's (dt-*), with this page's own rules after them
    '../macos-dock-town-page/macos-dock-town-page.component.scss',
    './macos-tidy-desktop-page.component.scss',
    // Add to cart with its price (cp-buy-row, cp-offer, cp-closing-buy), as on the Color Picker page (2026-10-14)
    '../macos-color-picker-page/macos-color-picker-page.component.scss',
  ],
} )
export class MacosTidyDesktopPageComponent extends ProductShots {

  readonly URLS = URLS;

  // Flip to true once the Mac App Store listing is public: until then the CTAs
  // say it's coming soon instead of linking to a listing that doesn't open yet.
  readonly released = true;   // live on the Mac App Store since 2026-10-27
  readonly appStoreUrl = 'https://apps.apple.com/app/tidy-desktop-ai-organizer/id6818882449';
  // Sold on the site too (2026-10-14), at the App Store's US price ($14.99): the notarized Developer ID build
  // through the cart. Until the store sells the sku (its catalogue entry is active with a Stripe price), the
  // page stays as it was, with the App Store as the only button.
  readonly storeSku = 'tidy-desktop/macos';

  // The product on its stage at the top of the page (product-hero.data.ts), and the facts as
  // chips under the pitch.
  readonly hero = PRODUCT_HEROES[ 'macos-tidy-desktop' ];
  readonly facts = [ 'macOS 26 Or Later', 'Using Apple Intelligence' ];   // owner 2026-10-10: two chips, no "Groups By Kind Without It"

  // What the app does, a section each with its headline and pictures (TIDY_DESKTOP_FEATURES, shared
  // with the home's showcase; owner 2026-10-10: as the Dock Town page): the first, the desktop before
  // and after, is the text and the pair right under the stage, the others follow.
  readonly intro: ProductFeature = TIDY_DESKTOP_FEATURES[ 0 ];
  readonly pictured: ProductFeature[] = TIDY_DESKTOP_FEATURES.slice( 1 );

  // What else it does, without a picture (what has one has its section above).
  readonly features = [
    {
      title: 'Your Files Never Move',
      description: 'Collections are a view, not folders. The app never moves, renames or deletes a file on its own, and quitting it leaves your desktop exactly as it was.',
    },
    {
      title: 'Finder\'s Right-Click Menu',
      description: 'Open With, Quick Look, Get Info, Rename, Tags and Move to Trash work on every file in a collection, and Move to Collection puts a file where you want it.',
    },
    {
      title: 'Locations',
      description: 'Downloads, Documents, Pictures, Music, Movies and Applications as windows on your desktop, showing their newest files. Drag files between them and your collections.',
    },
    {
      title: 'Made for the Desktop',
      description: 'Drag to move, resize from the corner, double-click to collapse. Collections line up from the edge of the screen, never overlap and make room when the screen fills up.',
    },
    {
      title: 'Works Without Apple Intelligence',
      description: 'On a Mac without Apple Intelligence, Tidy Desktop still tidies the desktop by kind: documents, images, screenshots, installers and the rest each get a collection of their own.',
    },
    {
      title: 'Private by Design',
      description: 'Grouping runs entirely on your Mac with Apple Intelligence. No account, no cloud and no network requests.',
    },
  ];

  // The stage picture first (it opens from the stage, product-hero.data.ts), then every scene of
  // every feature: each opens in the lightbox at full size (the -large file, loaded only then),
  // where the arrows page through them all and the picture's name shows under it.
  readonly shots: Shot[] = [
    {
      name: 'Tidy Desktop',
      src: this.hero.src,
      large: this.hero.src.replace( '.webp', '-large.webp' ),
      alt: this.hero.alt,
    },
    ...tidyDesktopShots(),
  ];

  // The questions the page shows, word for word the FAQPage JSON-LD the server sends
  // (TIDY_DESKTOP_FAQ in seo-route-meta.js): keep both in step.
  readonly faq = [
    { q: 'How do I organize my Mac desktop automatically?', a: 'Open Tidy Desktop and, when its welcome window asks, turn off Finder\'s own desktop icons (System Settings, Desktop & Dock, Show Items: On Desktop). Apple\'s on-device AI then looks at every file on your desktop and gathers it into a collection by what it is for, such as a trip, a project or invoices. New files join the right collection as they arrive, so there are no rules to write and nothing to drag.' },
    { q: 'Does Tidy Desktop move, rename or delete my files?', a: 'No. Collections are a view, not folders: every file stays where it is in your Desktop folder, under its own name. Tidy Desktop never moves, renames or deletes a file on its own, and when you quit it and show Finder\'s desktop icons again, your desktop is exactly as it was.' },
    { q: 'Is Tidy Desktop a Fences alternative for Mac?', a: 'Yes. Stardock Fences groups desktop icons on Windows; Tidy Desktop brings desktop collections to macOS and adds AI that fills them for you. Every collection can still be moved, resized, collapsed and recolored, and you can make your own by naming it and saying what it is for.' },
    { q: 'Does Tidy Desktop need Apple Intelligence?', a: 'AI Grouping runs on Apple\'s on-device Foundation Models, so it needs a Mac with Apple Intelligence turned on. Without it, Tidy Desktop still tidies the desktop by kind: documents, images, screenshots, installers and the rest each get a collection of their own.' },
    { q: 'Is Tidy Desktop private?', a: 'Yes. Grouping happens entirely on your Mac. There is no account and no cloud, and the app makes no network requests at all.' },
    { q: 'Can it organize Downloads and Documents too?', a: 'It can show them. Downloads, Documents, Pictures, Music, Movies and Applications can sit on the desktop as Locations, windows that show each folder\'s newest files. You can drag files between them and your collections, and Tidy Desktop never rearranges those folders on its own.' },
    { q: 'Which Macs does Tidy Desktop run on?', a: 'Macs with macOS 26 Tahoe or later. It is a native menu bar app, and macOS asks you before it reads your Desktop, Downloads or Documents.' },
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private cartPopupService: CartPopupService,
    private storeService: StoreService,
  ) {
    super();
  }

  /** the store sells the app on the site: the cart is the first button and the App Store the second */
  get sellsHere(): boolean {
    return !!STORE_APPS[ this.storeSku ] && this.storeService.availability( this.storeSku ) === 'buy';
  }

  /** "$14.99": the live Stripe price, else the list price */
  get priceLabel(): string | null {
    return this.storeService.priceLabel( this.storeSku, storeAppListPrice( this.storeSku ) );
  }

  /** puts the app in the cart and opens the mini cart (a toast on a phone); the page stays */
  addToCart(): void {
    this.cartPopupService.add( this.storeSku, 'macos-tidy-desktop-page' );
  }

  src( scene: ProductScene ): string {
    return tidyDesktopSceneSrc( scene );
  }

  /** The lightbox entry of a scene (the same object as in `shots`, so the arrows know where it is). */
  enlarge( scene: ProductScene ): void {
    this.openLightbox( this.shots.find( s => s.src === this.src( scene ) ) || tidyDesktopShot( scene ) );
  }

  trackFeature( _: number, feature: ProductFeature ): string {
    return feature.key;
  }

  trackScene( _: number, scene: ProductScene ): string {
    return scene.key;
  }

  onInstall( event?: Event ): void {
    this.analyticsService.logEvent( 'App Install Clicked', { app: 'macos-tidy-desktop' } );
    // on a Mac the App Store app opens at once (any browser); elsewhere the web listing
    openInMacAppStore( event, this.appStoreUrl );
  }
}
